import { CheckCircleTwoTone, LogoutOutlined } from "@ant-design/icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import {
  Button,
  Col,
  InputNumber,
  List,
  message,
  Row,
  Table,
  Typography,
} from "antd";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Loader } from "../../../../Components/Loader";
import { SuccessModal } from "../../../../Components/Modals/SuccessModal";
import { SkipButton } from "../../../../Components/SkipButton";
import { CustIcon } from "../../../../Components/Svgs";
import {
  DELETE_EMPLOYEE_COUNT,
  UPDATE_USER_LAST_SCREEN,
  UPSERT_EMPLOYEE_COUNT,
} from "../../../../Graphql/mutations";
import {
  GET_BUSINESS_BY_OWNER,
  GET_EMPLOYEE_ROLES_AND_LOCATIONS_FOR_BUSINESS,
  GET_USERS_COUNT_FOR_LOCATION,
} from "../../../../Graphql/queries";
import { logout } from "../../../../Utils/functions/common";
import "./EmployeeRoleManagement.less";

const { Title } = Typography;

export const EmployeeRoleManagementPage = () => {
  const history = useHistory();

  const [currentLocationId, setCurrentLocationId] = useState();
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedRoleKeys, setSelectedRoleKeys] = useState([]);
  const [allowContinue, setAllowContinue] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const user_id = localStorage.getItem("user_id");
  let business_id = "";

  const { data: businessDetails, loading: fetchingbusinessDetails } = useQuery(
    GET_BUSINESS_BY_OWNER,
    {
      variables: {
        owner_id: user_id,
      },
      fetchPolicy: "network-only",
    }
  );

  const [
    GET_EMPLOYEE_ROLES_AND_LOCATIONS_FOR_BUSINESS_QUERY,
    { data, loading: fetchingData, refetch, previousData },
  ] = useLazyQuery(GET_EMPLOYEE_ROLES_AND_LOCATIONS_FOR_BUSINESS, {
    variables: { user_id: user_id, business_id: business_id },
    fetchPolicy: "cache-and-network",
  });

  const [UPDATE_USER_LAST_SCREEN_MUTATION] = useMutation(
    UPDATE_USER_LAST_SCREEN
  );

  const {
    data: usersCountData,
    loading: fetchingUsersCountData,
    refetch: refetchUserCountData,
  } = useQuery(GET_USERS_COUNT_FOR_LOCATION, {
    variables: {
      owner_id: user_id,
    },
    fetchPolicy: "network-only",
  });

  const [DELETE_REMOVED_USER_ROLES, { loading: deletingUserRoles }] =
    useMutation(DELETE_EMPLOYEE_COUNT);

  const [UPSERT_EMPLOYEE_COUNT_MUTATION, { loading: upsertingUserRoles }] =
    useMutation(UPSERT_EMPLOYEE_COUNT);

  const onLocationChange = (locationIndex) => {
    setSelectedRoles([]);
    setSelectedRoleKeys([]);
    refetch();
    const currentLocId =
      data?.getBusinesses[0]?.user_location_maps[locationIndex]?.locationDetails
        ?.location_id;
    setCurrentLocationId(currentLocId);

    if (usersCountData?.users_for_location) {
      const existingRoles = _.compact(
        usersCountData?.users_for_location.map((x) => {
          if (x.location_id === currentLocId) return x;
        })
      );
      let alreadyAddedRolesData = existingRoles.flatMap(
        ({ count: totalCount, job_role: { role_id, role_name } }) => [
          {
            role_id,
            role_name,
            totalCount,
          },
        ]
      );
      setSelectedRoles(alreadyAddedRolesData);

      let alreadyAddedRolesKey = existingRoles.flatMap(
        ({ job_role: { role_id } }) => [role_id]
      );
      setSelectedRoleKeys(alreadyAddedRolesKey);
    }
  };

  useEffect(() => {
    if (businessDetails) {
      business_id = businessDetails?.business[0]?.business_id;
      GET_EMPLOYEE_ROLES_AND_LOCATIONS_FOR_BUSINESS_QUERY({
        variables: { user_id: user_id, business_id: business_id },
      });

      UPDATE_USER_LAST_SCREEN_MUTATION({
        variables: {
          user_id,
          last_screen: history.location.pathname,
        },
      });
    }
  }, [businessDetails]);

  useEffect(() => {
    if (data && usersCountData) {
      if (!previousData) {
        onLocationChange(0);
      }

      let continueDisable = true;
      data?.getBusinesses[0]?.user_location_maps.forEach(
        ({ locationDetails }) => {
          let addedInDB = locationDetails.savedCounts.aggregate.sum.count;
          if (addedInDB !== locationDetails.total_employees) {
            continueDisable = false;
          }
        }
      );
      setAllowContinue(continueDisable);
    }
  }, [data, usersCountData, fetchingData]);

  const rowSelection = {
    selectedRowKeys: selectedRoleKeys,
    onChange: (keys, rowValues) => {
      setSelectedRoleKeys(keys);
      rowValues.forEach((row) => {
        let matchingRow = selectedRoles.filter((r) => {
          return row.role_id === r.role_id;
        });
        if (matchingRow.length !== 0) {
          row.totalCount = matchingRow[0].totalCount;
        } else {
          delete row.totalCount;
        }
      });
      var dataset = [];
      rowValues.forEach((n) =>
        dataset.push({
          role_id: n.role_id,
          role_name: n.role_name,
          totalCount: n.totalCount,
        })
      );
      setSelectedRoles(dataset);
    },
  };

  const onRoleCountChange = (index, value) => {
    let tempVar = selectedRoles;
    tempVar[index].totalCount = parseInt(value);
    setSelectedRoles(tempVar);
    verifyMaxEmployeeCountForLocation().catch((maxCount) => {
      message.error(
        `You can add only ${maxCount} employees for selected location.`
      );
    });
  };

  const verifyMaxEmployeeCountForLocation = () => {
    let maxCount = data?.getBusinesses[0]?.user_location_maps.find(
      (singleLocation) => {
        return (
          singleLocation?.locationDetails?.location_id === currentLocationId
        );
      }
    )?.locationDetails?.total_employees;

    let currentTotal = 0;
    selectedRoles.forEach((n) => {
      if (n.totalCount) currentTotal += n.totalCount;
    });
    if (currentTotal > maxCount) {
      return Promise.reject(maxCount);
    }
    return Promise.resolve(true);
  };

  const onSaveRolesForLocation = () => {
    if (selectedRoles.length === 0) {
      return false;
    }
    let isEmptyOrZero = false;
    selectedRoles.forEach((role) => {
      if (!role.totalCount) {
        isEmptyOrZero = true;
      }
    });
    if (isEmptyOrZero) {
      message.error("Employee count cant be zero or empty");
      return false;
    }
    verifyMaxEmployeeCountForLocation()
      .then(async () => {
        let currentLocationData =
          data?.getBusinesses[0]?.user_location_maps.find((singleLocation) => {
            return (
              singleLocation?.locationDetails?.location_id === currentLocationId
            );
          });
        let isDeleteSuccess = await deleteRemovedUserRoles(currentLocationData);
        let isUpsertSuccess = await insertOrUpdateRoles(currentLocationData);

        if (isDeleteSuccess && isUpsertSuccess) {
          setShowSuccessModal(true);
          refetch();
          refetchUserCountData();
        }
      })
      .catch((maxCount) => {
        message.destroy();
        message.error(
          `You can add only ${maxCount} employees for selected location.`
        );
      });
  };

  const deleteRemovedUserRoles = async (currentLocation) => {
    const existingRoles = _.compact(
      usersCountData?.users_for_location.map((x) => {
        if (x.location_id === currentLocation.locationDetails.location_id)
          return x;
      })
    );
    let deletedRoles = existingRoles.filter(({ job_role: { role_id } }) => {
      return !selectedRoleKeys.includes(role_id);
    });

    if (deletedRoles.length !== 0) {
      var job_role_ids = deletedRoles.flatMap(({ job_role: { role_id } }) => [
        role_id,
      ]);

      // let requestParams = deletedRoles.flatMap(
      //   ({ job_role: { role_id } }) => [
      //     {
      //       business_id: data.getBusinesses[0].business_id,
      //       job_role_id: role_id,
      //       location_id: currentLocation.locationDetails.location_id
      //     },
      //   ]
      // );

      try {
        const result = await DELETE_REMOVED_USER_ROLES({
          variables: {
            business_id: data?.getBusinesses[0].business_id,
            job_role_id: job_role_ids[0],
            location_id: currentLocation.locationDetails.location_id,
          },
        });
        if (!result.errors) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        message.error("Something went wrong...");
        return false;
      }
    } else {
      return true;
    }
  };

  const insertOrUpdateRoles = async (currentLocationData) => {
    let updatedRoles = selectedRoles;
    updatedRoles.forEach((r) => {
      let matchingExistingData =
        currentLocationData?.locationDetails?.user_location_maps.filter((e) => {
          return e.job_role_id === r.role_id;
        });
      if (matchingExistingData.length !== 0) {
        r.id = matchingExistingData[0].job_role_id;
      }
    });
    let requestParams = updatedRoles.flatMap(({ id, totalCount, role_id }) => [
      {
        business_id: data?.getBusinesses[0].business_id,
        location_id: currentLocationData.locationDetails.location_id,
        job_role_id: role_id,
        count: totalCount,
      },
    ]);

    try {
      const result = await UPSERT_EMPLOYEE_COUNT_MUTATION({
        variables: {
          objects: requestParams,
        },
      });
      if (!result.error) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      message.error("Something went wrong...");
      return false;
    }
  };

  const onContinueClick = () => {
    history.push("/on-boarding/employee-details");
  };

  return (
    <div className="onBoardingRight">
      <div className="employeeRoles">
        <div className="pl-lg-5">
          <div className="d-flex justify-content-between align-items-start">
            <Title level={2}>
              <CustIcon type="left-arrow" onClick={() => history.goBack()} />
              Set up each of the employee roles you have in your organization
            </Title>
            <div
                className="d-flex align-items-center cursorPointer txtdark"
                onClick={() => logout()}
              >
                <CustIcon type="logout-alt" className="fz-18" />
                <span className="pl-2">Logout</span>
              </div>
          </div>
          <p className="subTitle">
            Select all employee roles and number of employees on each role.
          </p>
        </div>
        <Row gutter={16}>
          {fetchingData && !previousData ? (
            <Loader />
          ) : (
            <>
              <Col xs={24} sm={8} md={5} lg={5}>
                <div className="locationCard">
                  <div className="card-header">
                    <span>Select location</span>
                  </div>
                  {data && (
                    <List size="small" bordered>
                      {data?.getBusinesses[0]?.user_location_maps?.map(
                        (loc, index) => (
                          <List.Item
                            className={
                              currentLocationId ===
                              loc?.locationDetails?.location_id
                                ? "active"
                                : ""
                            }
                            key={index}
                            onClick={() => {
                              setTimeout(() => {
                                onLocationChange(index);
                              }, 1);
                            }}
                          >
                            <div className="d-flex justify-content-between w-100 align-items-center">
                              {loc?.locationDetails.location_name}
                              {loc?.locationDetails.savedCounts.aggregate.sum
                                .count ===
                                loc?.locationDetails.total_employees && (
                                <CheckCircleTwoTone twoToneColor="#8ac23b" />
                              )}
                            </div>
                          </List.Item>
                        )
                      )}
                    </List>
                  )}
                </div>
              </Col>
              <Col xs={24} sm={16} md={19}>
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <div className="locationCard employeeRolesCard">
                      <div className="card-header">
                        <span>Select employee roles</span>
                      </div>
                      <div className="card-body">
                        <Table
                          dataSource={data?.job_roles}
                          loading={fetchingData}
                          rowSelection={rowSelection}
                          showHeader={false}
                          pagination={false}
                          rowKey={(record) => record.role_id}
                        >
                          <Table.Column title="Role" dataIndex="role_name" />
                        </Table>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <div className="locationCard numEmployeeRolesCard">
                      <div className="card-header">
                        <span>Enter number of employee on each role</span>
                      </div>
                      <div className="card-body">
                        <Table
                          dataSource={selectedRoles}
                          loading={fetchingData}
                          showHeader={false}
                          pagination={false}
                          rowKey={(record) => record.role_id}
                        >
                          <Table.Column title="Role" dataIndex="role_name" />
                          <Table.Column
                            title="No. of employee"
                            key="totalCount"
                            render={(rowData, _record, index) => {
                              return (
                                <InputNumber
                                  placeholder="0"
                                  name="totalCount"
                                  className="totalCount"
                                  min={0}
                                  defaultValue={rowData.totalCount}
                                  onChange={(e) => onRoleCountChange(index, e)}
                                />
                              );
                            }}
                          />
                        </Table>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col xs={24}>
                <div className="pt-3 text-primary text-center">Please save your data before continuing. To go the next step, please press Continue.</div>
              </Col>
            </>
          )}
        </Row>

        <div className="buttonSec d-flex justify-content-center pt-4">
          <SkipButton />
          <Button
            loading={upsertingUserRoles || deletingUserRoles}
            type={"primary"}
            shape="round"
            size="large"
            onClick={onSaveRolesForLocation}
            className="mr-2"
            disabled={selectedRoles.length == 0}
          >
            Save
          </Button>
          <Button
            disabled={!allowContinue}
            type="default"
            className="ml-2"
            shape="round"
            size="large"
            htmlType="button"
            onClick={onContinueClick}
          >
            Continue
          </Button>
        </div>
      </div>
      {showSuccessModal && (
        <SuccessModal closeModal={() => setShowSuccessModal(false)} />
      )}
    </div>
  );
};
